/**
 * Push Notification Toggle Component
 * Allows users to enable/disable push notifications
 */

import { Bell, BellOff, BellRing, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useState } from 'react';
import { toast } from 'sonner';
import usePushNotifications from '@/hooks/usePushNotifications';

const PushNotificationToggle = ({ className = "" }) => {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTest
  } = usePushNotifications();

  const [showDialog, setShowDialog] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success('Notificaciones desactivadas');
      }
    } else {
      setShowDialog(true);
    }
  };

  const handleSubscribe = async () => {
    const success = await subscribe();
    if (success) {
      toast.success('Notificaciones activadas');
      setShowDialog(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    const success = await sendTest();
    setTesting(false);
    if (success) {
      toast.success('Notificación de prueba enviada');
    } else {
      toast.error('Error al enviar notificación de prueba');
    }
  };

  if (!isSupported) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-zinc-500 cursor-not-allowed h-8 w-8 md:h-10 md:w-10 p-0"
            disabled
          >
            <BellOff className="w-5 h-5 md:w-6 md:h-6" />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
          <p>Tu navegador no soporta notificaciones push</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggle}
            disabled={loading}
            className={`h-8 w-8 md:h-10 md:w-10 p-0 ${
              isSubscribed 
                ? 'text-green-400 hover:text-green-300' 
                : 'text-zinc-400 hover:text-zinc-100'
            } ${className}`}
            data-testid="push-toggle-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 md:w-6 md:h-6 animate-spin" />
            ) : isSubscribed ? (
              <BellRing className="w-5 h-5 md:w-6 md:h-6" />
            ) : (
              <Bell className="w-5 h-5 md:w-6 md:h-6" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
          <p>{isSubscribed ? 'Desactivar notificaciones' : 'Activar notificaciones push'}</p>
        </TooltipContent>
      </Tooltip>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Bell className="w-5 h-5 text-blue-400" />
              Notificaciones Push
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Recibe alertas críticas directamente en tu dispositivo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm text-zinc-200">Recibirás alertas para:</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Desborde de presas
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  Niveles críticos
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  Pérdida de conexión con sensores
                </li>
              </ul>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 rounded-lg p-3">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {permission === 'denied' && (
              <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 rounded-lg p-3">
                <AlertCircle className="w-4 h-4" />
                Los permisos de notificación están bloqueados. Por favor, habilítalos en la configuración de tu navegador.
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowDialog(false)}
              className="text-zinc-400"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubscribe}
              disabled={loading || permission === 'denied'}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Activando...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Activar Notificaciones
                </>
              )}
            </Button>
          </div>

          {isSubscribed && (
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTest}
                disabled={testing}
                className="w-full border-zinc-700 text-zinc-300"
              >
                {testing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <BellRing className="w-4 h-4 mr-2" />
                    Enviar notificación de prueba
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PushNotificationToggle;
